import type {ReactNode} from 'react';
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {RenderTreeNodePayload, TreeNodeData} from '@mantine/core';
import {
  Tree,
  UnstyledButton,
  getTreeExpandedState,
  useTree,
} from '@mantine/core';
import clsx from 'clsx';
import {IconChevronDown, IconChevronRight} from '@tabler/icons-react';
import classes from './HierarchyTree.module.css';

// Mantine 7.17 doesn't re-export its expanded-state shape from the package root.
type TreeExpandedState = Record<string, boolean>;

export interface HierarchyTreeNode {
  id: string;
  label: string;
  count?: number;
  icon?: ReactNode;
  adornment?: ReactNode;
  children?: HierarchyTreeNode[];
  hasChildren?: boolean;
}

export interface HierarchyTreeProps {
  nodes: HierarchyTreeNode[];
  selectedId?: string;
  onSelect: (id: string) => void;
  defaultExpandedIds?: string[];
  /** Controlled expansion. When provided, `onExpandedChange` receives every expand/collapse. */
  expandedIds?: string[];
  onExpandedChange?: (ids: string[]) => void;
}

const toTreeData = (nodes: HierarchyTreeNode[]): TreeNodeData[] =>
  nodes.map(node => ({
    value: node.id,
    label: node.label,
    children: node.children ? toTreeData(node.children) : undefined,
  }));

const buildNodeMap = (
  nodes: HierarchyTreeNode[],
  map: Map<string, HierarchyTreeNode> = new Map()
): Map<string, HierarchyTreeNode> => {
  nodes.forEach(node => {
    map.set(node.id, node);
    if (node.children) buildNodeMap(node.children, map);
  });
  return map;
};

interface HierarchyTreeRowProps {
  value: string;
  label: string;
  expanded: boolean;
  hasChildren: boolean;
  icon?: ReactNode;
  count?: number;
  adornment?: ReactNode;
  onToggle: (value: string) => void;
}

// Memoized row body: selection lives on the wrapper element, so a selection change
// re-renders no row body. Every prop is primitive or stable per `nodes` identity.
const HierarchyTreeRow = memo(
  ({
    value,
    label,
    expanded,
    hasChildren,
    icon,
    count,
    adornment,
    onToggle,
  }: HierarchyTreeRowProps) => (
    <>
      {hasChildren ? (
        <UnstyledButton
          aria-hidden="true"
          tabIndex={-1}
          onClick={event => {
            event.stopPropagation();
            onToggle(value);
          }}
          className={classes.toggle}>
          {expanded ? (
            <IconChevronDown size={16} />
          ) : (
            <IconChevronRight size={16} />
          )}
        </UnstyledButton>
      ) : (
        <span className={classes.togglePlaceholder} />
      )}
      {icon !== undefined && <span className={classes.icon}>{icon}</span>}
      <span className={classes.label}>{label}</span>
      {count !== undefined && <span className={classes.count}>{count}</span>}
      {adornment !== undefined && (
        <span className={classes.adornment}>{adornment}</span>
      )}
    </>
  )
);
HierarchyTreeRow.displayName = 'HierarchyTreeRow';

// Enter/Space selection isn't built into Mantine's Tree (only expand/check are) — keyboard
// focus lives on the `[role=treeitem]` li, so a wrapper-level listener reads its `data-value`.
const handleSelectKeyDown = (
  event: React.KeyboardEvent,
  onSelect: (id: string) => void
) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const target = event.target as HTMLElement;
  if (target.getAttribute('role') !== 'treeitem') return;
  event.preventDefault();
  const value = target.dataset.value;
  if (value) onSelect(value);
};

/**
 * Expandable, keyboard-navigable node tree with per-node counts and an adornment slot,
 * wrapping Mantine `Tree`; no built-in "All" root — callers supply one.
 * @registry Hierarchy tree with expand/collapse, single selection, aria-tree roles and arrow-key navigation — thin wrapper over Mantine `Tree`.
 */
export const HierarchyTree = ({
  nodes,
  selectedId,
  onSelect,
  defaultExpandedIds = [],
  expandedIds,
  onExpandedChange,
}: HierarchyTreeProps) => {
  const treeData = useMemo(() => toTreeData(nodes), [nodes]);
  const nodeMap = useMemo(() => buildNodeMap(nodes), [nodes]);

  const [initialExpandedState] = useState(() =>
    getTreeExpandedState(treeData, expandedIds ?? defaultExpandedIds)
  );
  const handleNodeExpand = useCallback(
    (id: string) => {
      if (!expandedIds || !onExpandedChange || expandedIds.includes(id)) return;
      onExpandedChange([...expandedIds, id]);
    },
    [expandedIds, onExpandedChange]
  );
  const handleNodeCollapse = useCallback(
    (id: string) => {
      if (!expandedIds || !onExpandedChange || !expandedIds.includes(id))
        return;
      onExpandedChange(expandedIds.filter(expandedId => expandedId !== id));
    },
    [expandedIds, onExpandedChange]
  );
  const tree = useTree({
    initialExpandedState,
    onNodeExpand: handleNodeExpand,
    onNodeCollapse: handleNodeCollapse,
  });
  const treeRef = useRef(tree);
  treeRef.current = tree;
  const toggleExpanded = useCallback(
    (value: string) => treeRef.current.toggleExpanded(value),
    []
  );

  // Controlled prop updates only push state into Mantine. User expansion changes are reported
  // by useTree's event callbacks, which avoids reporting stale state during this synchronization.
  useEffect(() => {
    if (!expandedIds) return;
    tree.setExpandedState(current => {
      // Set lookup, not Mantine's getTreeExpandedState: that is O(nodes x expandedIds).
      const wanted = new Set(expandedIds);
      const next: TreeExpandedState = {};
      let changed = false;
      for (const key of nodeMap.keys()) {
        next[key] = wanted.has(key);
        if (Boolean(current[key]) !== next[key]) changed = true;
      }
      return changed ? next : current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedIds, nodeMap]);

  // Sync Mantine's internal selection to the controlled `selectedId` prop so
  // `aria-selected` (set by Tree itself, not overridable via renderNode) stays accurate.
  useEffect(() => {
    if (selectedId) {
      tree.select(selectedId);
    } else {
      tree.clearSelected();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const renderNode = ({
    node,
    level,
    expanded,
    hasChildren,
    elementProps,
  }: RenderTreeNodePayload) => {
    const original = nodeMap.get(node.value);

    return (
      <div
        {...elementProps}
        onClick={event => {
          event.stopPropagation();
          onSelect(node.value);
        }}
        className={clsx(elementProps.className, classes.row)}
        data-row-selected={selectedId === node.value || undefined}
        style={{
          ...elementProps.style,
          paddingLeft: (level - 1) * 20 + 8,
        }}>
        <HierarchyTreeRow
          value={node.value}
          label={original?.label ?? node.value}
          expanded={expanded}
          hasChildren={hasChildren}
          icon={original?.icon}
          count={original?.count}
          adornment={original?.adornment}
          onToggle={toggleExpanded}
        />
      </div>
    );
  };

  return (
    <div onKeyDown={event => handleSelectKeyDown(event, onSelect)}>
      <Tree
        data={treeData}
        tree={tree}
        expandOnClick={false}
        expandOnSpace={false}
        renderNode={renderNode}
      />
    </div>
  );
};
